import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  Box,
  Button,
  Flex,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  Grid,
  IconButton,
  Input,
  InputGroup,
  InputRightElement,
  Text,
} from '@chakra-ui/react';
import { MdCalendarMonth, MdChevronLeft, MdChevronRight } from 'react-icons/md';
import { formatDobDisplay } from '../../utils/personName';

const WEEKDAYS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

function toIso(y, m, d) {
  return `${y}-${String(m + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
}

function parseIso(iso) {
  if (!iso || !/^\d{4}-\d{2}-\d{2}$/.test(iso)) return null;
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, m - 1, d);
}

/**
 * Mask typed input as DD/MM/YYYY while typing.
 * Digits only; inserts `/` after day and month.
 */
export function maskDateInput(raw) {
  const digits = String(raw || '')
    .replace(/\D/g, '')
    .slice(0, 8);
  if (digits.length <= 2) return digits;
  if (digits.length <= 4) return `${digits.slice(0, 2)}/${digits.slice(2)}`;
  return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
}

/** Accepts dd/mm/yyyy (masked or loose) → ISO YYYY-MM-DD, else ''. */
export function parseTypedDate(text) {
  const raw = String(text || '').trim();
  if (!raw) return '';
  const digits = raw.replace(/\D/g, '');
  let d;
  let m;
  let y;
  const parts = raw.split(/[/\-.\s]+/).filter(Boolean);
  if (parts.length === 3) {
    [d, m, y] = parts.map(Number);
  } else if (digits.length === 8) {
    d = Number(digits.slice(0, 2));
    m = Number(digits.slice(2, 4));
    y = Number(digits.slice(4));
  } else {
    return '';
  }
  if (!d || !m || !y) return '';
  if (y < 100) y += y > 50 ? 1900 : 2000;
  if (m < 1 || m > 12 || d < 1 || d > 31 || y < 1900 || y > 2200) return '';
  const dt = new Date(y, m - 1, d);
  if (dt.getFullYear() !== y || dt.getMonth() !== m - 1 || dt.getDate() !== d) return '';
  return toIso(y, m - 1, d);
}

/** Client-side date field check (ISO or typed DD/MM/YYYY). */
export function validateDateField(value, { required = false, min, max, label = 'Date' } = {}) {
  const raw = String(value || '').trim();
  if (!raw) return required ? `${label} is required` : '';
  const iso = /^\d{4}-\d{2}-\d{2}$/.test(raw) ? raw : parseTypedDate(raw);
  if (!iso) return `Enter a valid ${label.toLowerCase()} (DD/MM/YYYY)`;
  if (min && iso < min) return `${label} is too early`;
  if (max && iso > max) return `${label} is too late`;
  return '';
}

/**
 * Themed date picker — fixed calendar portaled to document.body.
 * Typing auto-masks as DD/MM/YYYY; value stored as ISO YYYY-MM-DD.
 */
export function AppDatePicker({
  label = 'Date of Birth',
  value,
  onChange,
  isRequired = false,
  isInvalid = false,
  errorMessage,
  helperText,
  min,
  max,
}) {
  const selected = parseIso(value);
  const [view, setView] = useState(() => selected || parseIso(max) || new Date());
  const [open, setOpen] = useState(false);
  const [text, setText] = useState('');
  const [typing, setTyping] = useState(false);
  const [localError, setLocalError] = useState('');
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const triggerRef = useRef(null);
  const panelRef = useRef(null);

  const minDate = parseIso(min);
  const maxDate = parseIso(max);

  useEffect(() => {
    if (typing) return;
    const formatted = value ? formatDobDisplay(value) : '';
    setText(formatted === '—' ? '' : formatted);
    setLocalError('');
  }, [value, typing]);

  const isIsoAllowed = (iso) => {
    if (min && iso < min) return false;
    if (max && iso > max) return false;
    return true;
  };

  const commit = (iso) => {
    setTyping(false);
    setLocalError('');
    onChange?.({ target: { value: iso } });
  };

  const handleType = (e) => {
    const masked = maskDateInput(e.target.value);
    setText(masked);
    setLocalError('');
    setTyping(true);

    if (!masked) {
      onChange?.({ target: { value: '' } });
      return;
    }

    // Commit only when full DD/MM/YYYY is typed
    const digits = masked.replace(/\D/g, '');
    if (digits.length < 8) {
      // Keep parent cleared while incomplete so invalid partials aren't saved
      if (value) onChange?.({ target: { value: '' } });
      return;
    }

    const iso = parseTypedDate(masked);
    if (iso && isIsoAllowed(iso)) {
      onChange?.({ target: { value: iso } });
      setView(parseIso(iso));
      setLocalError('');
    } else {
      onChange?.({ target: { value: '' } });
      setLocalError('Enter a valid date (DD/MM/YYYY)');
    }
  };

  const handleBlur = () => {
    const digits = String(text || '').replace(/\D/g, '');
    if (!digits) {
      commit('');
      return;
    }
    if (digits.length < 8) {
      setLocalError('Enter full date as DD/MM/YYYY');
      setTyping(false);
      onChange?.({ target: { value: '' } });
      return;
    }
    const iso = parseTypedDate(text);
    if (iso && isIsoAllowed(iso)) {
      commit(iso);
      setText(formatDobDisplay(iso));
    } else {
      setLocalError('Enter a valid date (DD/MM/YYYY)');
      setTyping(false);
      onChange?.({ target: { value: '' } });
    }
  };

  const days = useMemo(() => {
    const y = view.getFullYear();
    const m = view.getMonth();
    const firstDow = new Date(y, m, 1).getDay();
    const daysInMonth = new Date(y, m + 1, 0).getDate();
    const cells = [];
    for (let i = 0; i < firstDow; i += 1) cells.push(null);
    for (let d = 1; d <= daysInMonth; d += 1) cells.push(d);
    while (cells.length % 7 !== 0) cells.push(null);
    return cells;
  }, [view]);

  const updatePosition = () => {
    const el = triggerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const panelW = 268;
    const gap = 4;
    let left = rect.left;
    let top = rect.bottom + gap;
    const maxLeft = window.innerWidth - panelW - 8;
    if (left > maxLeft) left = Math.max(8, maxLeft);
    if (left < 8) left = 8;
    const estimatedH = 268;
    if (top + estimatedH > window.innerHeight - 8 && rect.top > estimatedH) {
      top = rect.top - estimatedH - gap;
    }
    setPos({ top, left });
  };

  useLayoutEffect(() => {
    if (!open) return undefined;
    updatePosition();
    const onReposition = () => updatePosition();
    window.addEventListener('resize', onReposition);
    window.addEventListener('scroll', onReposition, true);
    return () => {
      window.removeEventListener('resize', onReposition);
      window.removeEventListener('scroll', onReposition, true);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (e) => {
      const t = e.target;
      if (triggerRef.current?.contains(t)) return;
      if (panelRef.current?.contains(t)) return;
      setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('touchstart', onPointerDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('touchstart', onPointerDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const isDisabledDay = (d) => {
    if (d == null) return true;
    const dt = new Date(view.getFullYear(), view.getMonth(), d);
    if (minDate && dt < minDate) return true;
    if (maxDate && dt > maxDate) return true;
    return false;
  };

  const isSelectedDay = (d) => {
    if (!selected || d == null) return false;
    return (
      selected.getFullYear() === view.getFullYear() &&
      selected.getMonth() === view.getMonth() &&
      selected.getDate() === d
    );
  };

  const pickDay = (d) => {
    if (isDisabledDay(d)) return;
    commit(toIso(view.getFullYear(), view.getMonth(), d));
    setOpen(false);
  };

  const clear = () => {
    commit('');
    setText('');
    setOpen(false);
  };

  const openCalendar = (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    setView(selected || parseIso(max) || new Date());
    setOpen((v) => !v);
  };

  const showError = isInvalid || Boolean(localError);
  const shownError = errorMessage || localError;

  const calendar = open
    ? createPortal(
        <Box
          ref={panelRef}
          position="fixed"
          top={`${pos.top}px`}
          left={`${pos.left}px`}
          w="268px"
          zIndex={4000}
          bg="bg.surface"
          borderWidth="1px"
          borderColor="border.subtle"
          borderRadius="md"
          boxShadow="lg"
          p={2}
          onMouseDown={(e) => e.stopPropagation()}
        >
          <Flex align="center" justify="space-between" mb={1.5}>
            <IconButton
              aria-label="Previous month"
              icon={<MdChevronLeft size={18} />}
              size="xs"
              variant="ghostInk"
              type="button"
              minW="28px"
              h="28px"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() =>
                setView((v) => new Date(v.getFullYear(), v.getMonth() - 1, 1))
              }
            />
            <Text fontWeight="700" fontSize="13px">
              {MONTHS[view.getMonth()]} {view.getFullYear()}
            </Text>
            <IconButton
              aria-label="Next month"
              icon={<MdChevronRight size={18} />}
              size="xs"
              variant="ghostInk"
              type="button"
              minW="28px"
              h="28px"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() =>
                setView((v) => new Date(v.getFullYear(), v.getMonth() + 1, 1))
              }
            />
          </Flex>

          <Grid templateColumns="repeat(7, 1fr)" gap="2px" mb={0.5}>
            {WEEKDAYS.map((w, i) => (
              <Text
                key={`${w}-${i}`}
                textAlign="center"
                fontSize="10px"
                fontWeight="700"
                color="text.secondary"
                lineHeight="18px"
              >
                {w}
              </Text>
            ))}
          </Grid>

          <Grid templateColumns="repeat(7, 1fr)" gap="2px">
            {days.map((d, i) => {
              if (d == null) return <Box key={`e-${i}`} h="28px" />;
              const disabled = isDisabledDay(d);
              const selectedDay = isSelectedDay(d);
              return (
                <Button
                  key={`d-${d}`}
                  type="button"
                  h="28px"
                  minW="28px"
                  p={0}
                  fontSize="12px"
                  fontWeight={selectedDay ? '700' : '500'}
                  borderRadius="sm"
                  variant="unstyled"
                  isDisabled={disabled}
                  bg={selectedDay ? 'utkarsh.amber' : 'transparent'}
                  color={
                    selectedDay
                      ? 'text.onAmber'
                      : disabled
                        ? 'text.placeholder'
                        : 'text.primary'
                  }
                  _hover={
                    disabled
                      ? {}
                      : {
                          bg: selectedDay ? 'utkarsh.amber' : 'bg.amberTint',
                        }
                  }
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => pickDay(d)}
                >
                  {d}
                </Button>
              );
            })}
          </Grid>

          <Flex
            justify="space-between"
            align="center"
            mt={1.5}
            pt={1.5}
            borderTopWidth="1px"
            borderColor="border.subtle"
          >
            <Button
              type="button"
              size="xs"
              h="28px"
              variant="ghost"
              color="text.secondary"
              onMouseDown={(e) => e.preventDefault()}
              onClick={clear}
            >
              Clear
            </Button>
            <Button
              type="button"
              size="xs"
              h="28px"
              px={3}
              bg="bg.amberTint"
              color="text.primary"
              fontWeight="600"
              _hover={{ bg: 'utkarsh.amber', color: 'text.onAmber' }}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => {
                const today = new Date();
                const iso = toIso(
                  today.getFullYear(),
                  today.getMonth(),
                  today.getDate(),
                );
                if (!isIsoAllowed(iso)) return;
                commit(iso);
                setView(today);
                setOpen(false);
              }}
            >
              Today
            </Button>
          </Flex>
        </Box>,
        document.body,
      )
    : null;

  return (
    <FormControl isRequired={isRequired} isInvalid={showError}>
      {label ? <FormLabel>{label}</FormLabel> : null}
      <Box ref={triggerRef}>
        <InputGroup size="lg">
          <Input
            value={text}
            placeholder="DD/MM/YYYY"
            inputMode="numeric"
            autoComplete="off"
            maxLength={10}
            h="52px"
            fontSize="15px"
            fontWeight="500"
            pr="48px"
            onFocus={() => setTyping(true)}
            onChange={handleType}
            onBlur={handleBlur}
            onKeyDown={(e) => {
              // Allow navigation / edit keys; block non-digit chars (mask adds /)
              if (
                e.key.length === 1 &&
                !/\d/.test(e.key) &&
                !e.ctrlKey &&
                !e.metaKey &&
                !e.altKey
              ) {
                e.preventDefault();
              }
            }}
          />
          <InputRightElement h="52px">
            <IconButton
              aria-label="Open calendar"
              icon={<MdCalendarMonth size={22} />}
              type="button"
              size="sm"
              variant="ghostInk"
              color="text.secondary"
              onMouseDown={openCalendar}
            />
          </InputRightElement>
        </InputGroup>
      </Box>
      {calendar}
      {showError && shownError ? (
        <FormErrorMessage>{shownError}</FormErrorMessage>
      ) : helperText ? (
        <FormHelperText color="text.secondary">{helperText}</FormHelperText>
      ) : (
        <FormHelperText color="text.secondary">Format: DD/MM/YYYY</FormHelperText>
      )}
    </FormControl>
  );
}
